import { DeleteOutlined, PlayCircleOutlined, PlusCircleFilled } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import { CONSTANT } from '@salescore/client-base'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  type ConditionalEffectConfig,
  conditionalEffectConfigSchema,
  CoreDsl,
  type ViewConfigField,
  type ViewQueryRecordNode,
} from '@salescore/core'
// eslint-disable-next-line sonarjs/deprecation
import { JsonSyntaxHighlighter, useBooleanState, useModal } from '@salescore/frontend-common'
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Empty,
  Form,
  type FormInstance,
  type FormListFieldData,
  message,
  Modal,
  type ModalFuncProps,
  Row,
  Select,
  Space,
} from 'antd'
import { t } from 'i18next'
import { type ReactElement, useState } from 'react'
import { z } from 'zod'

import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column'
import { useGetInnerCells } from '../../../recoil/cursor'
import { useSetFieldMutation } from '../../../recoil/viewConfig'
import { ConditionalEffectExpressionEditor } from '../editor/ConditionalEffectExpressionEditor'

export const buildConditionalEffectsFormModalProperties = ({
  configField,
  onAfterFinish,
  firstRecord,
  column,
  compiledConfig,
  setHasChangeToViewSchema,
}: {
  configField: ViewConfigField
  onAfterFinish: () => void
  firstRecord: ViewQueryRecordNode
  column: RSheetColumn<RSheetRecordNode>
  // eslint-disable-next-line sonarjs/use-type-alias
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ModalFuncProps => ({
  icon: null,
  width: '80%',
  okButtonProps: { style: { display: 'none' } },
  cancelText: t(`閉じる`),
  maskClosable: true,
  title: <PageHeader title={t(`条件付き書式設定`)} />,
  content: (
    <ConditionalEffectsForm
      configField={configField}
      onAfterFinish={onAfterFinish}
      firstRecord={firstRecord}
      column={column}
      compiledConfig={compiledConfig}
      setHasChangeToViewSchema={setHasChangeToViewSchema}
    />
  ),
  style: {
    maxWidth: 1000,
    padding: 0,
  },
  destroyOnClose: true,
  footer: null,
  closable: true,
})

const defaultEffect: ConditionalEffectConfig = {
  effect: 'disable',
  expression: `equal(1, 1)`,
}

const ConditionalEffectsForm = ({
  configField,
  onAfterFinish,
  firstRecord,
  column,
  compiledConfig,
  setHasChangeToViewSchema,
}: {
  configField: ViewConfigField
  onAfterFinish: () => void
  firstRecord: ViewQueryRecordNode
  column: RSheetColumn<RSheetRecordNode>
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const { setField } = {
    setField: useSetFieldMutation(),
  }
  const effects = configField.conditionalEffects ?? []
  const [form] = Form.useForm()

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        effects: effects.length > 0 ? effects : [defaultEffect],
      }}
      onFinish={(values) => {
        const effects = z.object({ effects: conditionalEffectConfigSchema.array() }).safeParse(values)
        if (!effects.success) {
          void message.error(t(`書式が間違っています`))
          return
        }
        setField({
          field: {
            ...configField,
            conditionalEffects: effects.data.effects,
          },
          onSetConfig: () => {
            setHasChangeToViewSchema(true)
          },
        })
        void message.success(t(`条件付き書式を設定しました`))
        onAfterFinish()
      }}
    >
      <Form.List name="effects">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, index) => (
              // eslint-disable-next-line @eslint-react/no-missing-key
              <>
                <FormItems
                  field={field}
                  configField={configField}
                  form={form}
                  remove={remove}
                  firstRecord={firstRecord}
                  column={column}
                  compiledConfig={compiledConfig}
                />
                {fields.length - 1 !== index && <Divider />}
              </>
            ))}
            <Row justify="center">
              <Space>
                <Button
                  type="text"
                  icon={<PlusCircleFilled />}
                  color={'blue'}
                  style={{
                    color: CONSTANT.colors.primaryColor,
                  }}
                  onClick={() => {
                    add(defaultEffect)
                  }}
                >
                  {t(`効果を追加`)}
                </Button>
              </Space>
            </Row>
          </>
        )}
      </Form.List>
      <Divider />
      <Row justify="end" className="mt-4">
        <Space>
          <Button htmlType="submit">{t(`適用`)}</Button>
        </Space>
      </Row>
    </Form>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function FormItems({
  form,
  field,
  configField,
  remove,
  firstRecord,
  column,
  compiledConfig,
}: {
  form: FormInstance
  field: FormListFieldData
  configField: ViewConfigField
  remove: (index: number) => void
  firstRecord: ViewQueryRecordNode
  column: RSheetColumn<RSheetRecordNode>
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
}) {
  const [result, setResult] = useState<unknown>()
  const getInnerCells = useGetInnerCells(undefined)
  const sheetCellGroup = getInnerCells(firstRecord, column)
  const recordNode = sheetCellGroup[0]?.recordNode
  // eslint-disable-next-line sonarjs/deprecation
  const confirmModal = useModal()
  // eslint-disable-next-line sonarjs/deprecation
  const shouldIgnoreModal = useBooleanState()

  const execute = () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const expression = form.getFieldValue([`effects`, field.name, `expression`]) as string
    if (typeof expression !== 'string') {
      void message.warning(t(`式を定義してください`))
      return
    }
    if (recordNode === undefined) {
      return
    }

    try {
      const result = CoreDsl.executeWithRecordNode(expression, recordNode)
      if (result.success) {
        setResult(result.data)
      } else {
        setResult('')
        void message.error(`${t(`エラーが発生しました。`)}${JSON.stringify(result.error)}`)
      }
    } catch (error) {
      if (error instanceof Error) {
        void message.error(`${t(`エラーが発生しました。`)}${error.message}`)
      } else {
        void message.error(t(`エラーが発生しました。`))
      }
    }
  }

  const height = 160
  return (
    <Row>
      <Col span={8}>
        <Form.Item
          label={t(`効果`)}
          name={[field.name, 'effect']}
          rules={[{ required: true, message: t(`効果を入力してください`) }]}
        >
          <Select
            options={[
              {
                value: `disable`,
                label: t(`書き込み禁止`),
              },
              {
                value: `emphasis`,
                label: t(`強調`),
              },
            ]}
          />
        </Form.Item>
      </Col>
      <Col span={17}>
        {/* eslint-disable-next-line sonarjs/todo-tag */}
        {/* TODO: 中途半端にコンポーネント分割してしまった感 */}
        <ConditionalEffectExpressionEditor
          field={field}
          configField={configField}
          height={height}
          onExecute={() => {
            if (shouldIgnoreModal.isTrue) {
              execute()
            } else {
              confirmModal.showModal()
            }
          }}
        />
      </Col>
      <Col
        span={6}
        className=""
        style={{
          backgroundColor: `#2E2E2E`,
          color: `white`,
          height,
          borderLeft: `1px solid #444`,
        }}
      >
        <div
          className="flex items-center px-2 align-middle opacity-80"
          style={{ height: 32, borderBottom: `1px solid #444` }}
        >
          {t(`実行結果`)}
        </div>
        {/* <Button onClick={execute}>実行</Button> */}
        <div className="p-2">
          {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            JSON.stringify(result) ?? ''
          }
        </div>
      </Col>
      <Col span={1}>
        <Button
          className="mt-16"
          icon={<DeleteOutlined />}
          size="large"
          type="text"
          onClick={() => {
            remove(field.name)
          }}
        />
      </Col>
      <Modal
        open={confirmModal.isModalVisible}
        onCancel={confirmModal.hideModal}
        width={'60%'}
        cancelText={t(`閉じる`)}
        okButtonProps={{ style: { display: 'none' } }}
        title={<div></div>}
        footer={null}
        // style={{ top: '3%' }}
      >
        <Alert
          type="info"
          showIcon
          description={<div>{t(`現在の列の1行目のレコードの値を変数として、定義した関数を実行します。`)}</div>}
        />
        <Row justify="end" className="my-4">
          <Space className="mr-4">
            <Checkbox
              onChange={(e) => {
                shouldIgnoreModal.toggle()
              }}
            />
            {t(`このメッセージを今後表示しない`)}
          </Space>

          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={() => {
              execute()
              confirmModal.hideModal()
            }}
          >
            {t(`実行`)}
          </Button>
        </Row>

        <div
          style={{
            maxHeight: 500,
            overflowY: 'scroll',
          }}
        >
          <div className="mb-2">{t(`変数として代入される値`)}</div>
          {recordNode === undefined ? (
            <Empty description={t(`レコードがないため、変数がない状態で実行します。`)} />
          ) : (
            <JsonSyntaxHighlighter jsonString={JSON.stringify(recordNode)} />
          )}
        </div>
      </Modal>
    </Row>
  )
}
